import type {
  AudienceReply,
  ParticipantState,
  RoomThread,
} from "../../shared/types.ts";
import { totalUpvotes } from "../lib/threads.ts";

type LikeTransition = {
  liked: boolean;
  delta: 1 | -1;
};

export function optimisticallyTogglePostLike(
  state: ParticipantState,
  postId: string,
): ParticipantState {
  const current = findThread(state, postId);
  if (!current) return state;

  const transition = likeTransition(current.likedByParticipant);
  const updateThread = (thread: RoomThread) =>
    thread.id === postId ? updateThreadLike(thread, transition) : thread;

  return mapParticipantThreads(state, updateThread);
}

export function optimisticallyToggleReplyLike(
  state: ParticipantState,
  replyId: string,
): ParticipantState {
  const current = findReply(state, replyId);
  if (!current) return state;

  const transition = likeTransition(current.likedByParticipant);
  const updateReply = (reply: AudienceReply) =>
    reply.id === replyId ? updateLike(reply, transition) : reply;
  const updateThread = (thread: RoomThread) => {
    if (!thread.replies?.some((reply) => reply.id === replyId)) return thread;
    return {
      ...mapThreadReplies(thread, updateReply),
      threadLikeCount: Math.max(0, totalUpvotes(thread) + transition.delta),
    };
  };

  return {
    ...mapParticipantThreads(state, updateThread),
    pendingReplies: state.pendingReplies.map((pending) => ({
      reply: updateReply(pending.reply),
      parentThread: updateThread(pending.parentThread),
    })),
  };
}

function likeTransition(likedByParticipant: boolean | undefined): LikeTransition {
  const liked = !Boolean(likedByParticipant);
  return { liked, delta: liked ? 1 : -1 };
}

function updateLike<T extends { likeCount: number; likedByParticipant?: boolean }>(
  item: T,
  transition: LikeTransition,
): T {
  return {
    ...item,
    likeCount: Math.max(0, item.likeCount + transition.delta),
    likedByParticipant: transition.liked,
  };
}

function updateThreadLike(thread: RoomThread, transition: LikeTransition): RoomThread {
  return {
    ...updateLike(thread, transition),
    threadLikeCount: Math.max(0, totalUpvotes(thread) + transition.delta),
  };
}

function mapParticipantThreads(
  state: ParticipantState,
  updateThread: (thread: RoomThread) => RoomThread,
): ParticipantState {
  return {
    ...state,
    threads: state.threads.map(updateThread),
    pendingThreads: state.pendingThreads.map(updateThread),
    pendingReplies: state.pendingReplies.map((pending) => ({
      ...pending,
      parentThread: updateThread(pending.parentThread),
    })),
    projectedContent: state.projectedContent?.type === "thread"
      ? { ...state.projectedContent, thread: updateThread(state.projectedContent.thread) }
      : state.projectedContent,
  };
}

function mapThreadReplies(
  thread: RoomThread,
  updateReply: (reply: AudienceReply) => AudienceReply,
): RoomThread {
  if (!thread.replies) return thread;
  return { ...thread, replies: thread.replies.map(updateReply) };
}

function findThread(state: ParticipantState, postId: string): RoomThread | undefined {
  if (state.projectedContent?.type === "thread" && state.projectedContent.thread.id === postId) {
    return state.projectedContent.thread;
  }
  return state.threads.find((thread) => thread.id === postId)
    ?? state.pendingThreads.find((thread) => thread.id === postId)
    ?? state.pendingReplies.find((pending) => pending.parentThread.id === postId)?.parentThread;
}

function findReply(state: ParticipantState, replyId: string): AudienceReply | undefined {
  const pending = state.pendingReplies.find((row) => row.reply.id === replyId);
  if (pending) return pending.reply;

  for (const thread of participantThreads(state)) {
    const reply = thread.replies?.find((candidate) => candidate.id === replyId);
    if (reply) return reply;
  }
  return undefined;
}

function participantThreads(state: ParticipantState): RoomThread[] {
  return [
    ...(state.projectedContent?.type === "thread" ? [state.projectedContent.thread] : []),
    ...state.threads,
    ...state.pendingThreads,
    ...state.pendingReplies.map((pending) => pending.parentThread),
  ];
}
